/**
 * Fetch terrain elevation data from the OpenTopoData SRTM API and expose a
 * bilinear-interpolation function usable anywhere in the scene.
 *
 * Grid: 10×10 = 100 points (matches the 100 locations/request API limit).
 * Centre elevation is subtracted so the scene origin (0, 0) stays near y=0.
 */

const OPENTOPODATA_URL = 'https://api.opentopodata.org/v1/srtm30m';
const GRID_N          = 10;
const CACHE_TTL_MS    = 7 * 24 * 60 * 60 * 1000; // 1 week

/**
 * Fetch a 10×10 elevation grid and return an elevFn(sceneX, sceneZ) → metres.
 * On API failure, silently falls back to a flat terrain (elevFn always returns 0).
 *
 * @param {number} lat          Centre latitude (WGS84)
 * @param {number} lng          Centre longitude (WGS84)
 * @param {number} radiusMeters Half-width of the loaded area
 */
export async function fetchElevation(lat, lng, radiusMeters) {
  const latScale = 111319;                                 // metres per degree of latitude
  const lonScale = 111319 * Math.cos(lat * Math.PI / 180); // metres per degree of longitude
  const latDelta = radiusMeters / latScale;                // degrees of latitude for radius
  const lonDelta = radiusMeters / lonScale;                // degrees of longitude for radius

  // Cache key
  const key = `osm3d:elev:${(lat).toFixed(3)},${(lng).toFixed(3)},${radiusMeters}`;

  // Try localStorage cache
  let elevGrid = null;
  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const cached = JSON.parse(raw);
      if (Date.now() - cached.timestamp < CACHE_TTL_MS) {
        elevGrid = cached.grid;
      } else {
        localStorage.removeItem(key);
      }
    }
  } catch { /* corrupt cache – ignore */ }

  if (!elevGrid) {
    elevGrid = await _fetchFromAPI(lat, lng, latDelta, lonDelta);

    // Subtract centre elevation so scene y=0 ≈ ground level at start
    const ci = Math.floor(GRID_N / 2);
    const centerElev = elevGrid[ci][ci];
    for (let gi = 0; gi < GRID_N; gi++)
      for (let gj = 0; gj < GRID_N; gj++)
        elevGrid[gi][gj] -= centerElev;

    try {
      localStorage.setItem(key, JSON.stringify({ grid: elevGrid, timestamp: Date.now() }));
    } catch { /* quota – ignore */ }
  }

  const radiusX = lonDelta * lonScale; // metres east-west
  const radiusZ = latDelta * latScale; // metres north-south (= radiusMeters)

  return _makeElevFn(elevGrid, radiusX, radiusZ);
}

// ── Internal ──────────────────────────────────────────────────────────────────

async function _fetchFromAPI(lat, lng, latDelta, lonDelta) {
  // Build flat list of (gi, gj) → lat/lon points
  const points = [];
  for (let gi = 0; gi < GRID_N; gi++) {
    for (let gj = 0; gj < GRID_N; gj++) {
      // gi=0 → north (+latDelta), gi=9 → south (-latDelta)
      const pLat = lat + latDelta - gi * (2 * latDelta / (GRID_N - 1));
      // gj=0 → west (-lonDelta), gj=9 → east (+lonDelta)
      const pLon = lng - lonDelta + gj * (2 * lonDelta / (GRID_N - 1));
      points.push({ gi, gj, lat: pLat, lon: pLon });
    }
  }

  const locations = points.map(p => `${p.lat.toFixed(6)},${p.lon.toFixed(6)}`).join('|');

  try {
    const res = await fetch(`${OPENTOPODATA_URL}?locations=${locations}`, {
      signal: AbortSignal.timeout(10000), // 10 s timeout
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    if (json.status !== 'OK' || !Array.isArray(json.results)) throw new Error('Bad response');

    const grid = Array.from({ length: GRID_N }, () => new Array(GRID_N).fill(0));
    for (let i = 0; i < points.length; i++) {
      const { gi, gj } = points[i];
      grid[gi][gj] = json.results[i]?.elevation ?? 0;
    }
    return grid;
  } catch (err) {
    console.warn('[elevation] Fetch failed, using flat terrain:', err.message);
    return Array.from({ length: GRID_N }, () => new Array(GRID_N).fill(0));
  }
}

/**
 * Create a bilinear-interpolation closure over the elevation grid.
 *
 * Grid layout:
 *   gi = row: 0 = north edge (scene z = -radiusZ), GRID_N-1 = south edge (scene z = +radiusZ)
 *   gj = col: 0 = west  edge (scene x = -radiusX), GRID_N-1 = east  edge (scene x = +radiusX)
 */
function _makeElevFn(grid, radiusX, radiusZ) {
  const last = GRID_N - 1;

  return function elevFn(x, z) {
    // Normalised grid coordinates [0, last]
    const gj = (x / radiusX + 1) * 0.5 * last; // west→east  = -radiusX→+radiusX
    const gi = (z / radiusZ + 1) * 0.5 * last; // north→south = -radiusZ→+radiusZ

    // Clamp to valid bilinear quad
    const gi0 = Math.max(0, Math.min(last - 1, Math.floor(gi)));
    const gj0 = Math.max(0, Math.min(last - 1, Math.floor(gj)));
    const gi1 = gi0 + 1;
    const gj1 = gj0 + 1;

    const ti = gi - gi0; // fraction [0,1] along row
    const tj = gj - gj0; // fraction [0,1] along col

    return (
      grid[gi0][gj0] * (1 - ti) * (1 - tj) +
      grid[gi0][gj1] * (1 - ti) *      tj  +
      grid[gi1][gj0] *      ti  * (1 - tj) +
      grid[gi1][gj1] *      ti  *      tj
    );
  };
}
